import { Chart as ChartJS } from 'chart.js';

import type { ChartConfig } from './useChartItemsVisibility';

/**
 * Query-string parameter carrying the chart legend items hidden by the user,
 * so the export tab (a fresh page driven only by the URL) can reproduce them.
 * Format: `<chartId>:<index>,<index>;<chartId>:<index>`
 */
export const HIDDEN_CHART_ITEMS_PARAM = 'hiddenChartItems';

export type HiddenChartItems = Record<string, number[]>;

export function serializeHiddenChartItems(hiddenItems: HiddenChartItems): string {
  return Object.entries(hiddenItems)
    .map(([chartId, indexes]) => `${chartId}:${indexes.join(',')}`)
    .join(';');
}

export function parseHiddenChartItems(search: string): HiddenChartItems {
  const serialized = new URLSearchParams(search).get(HIDDEN_CHART_ITEMS_PARAM);
  if (!serialized) return {};

  // null prototype: chart ids come from the URL, so keys like "constructor" must not resolve to inherited values
  return serialized.split(';').reduce<HiddenChartItems>((hiddenItems, entry) => {
    const [chartId, indexesList = ''] = entry.split(':');
    if (!chartId) return hiddenItems;

    const indexes = indexesList
      .split(',')
      .filter((indexToken) => indexToken !== '') // Number('') is 0 — an empty token must not hide index 0
      .map(Number)
      .filter((index) => Number.isInteger(index) && index >= 0);
    if (indexes.length) hiddenItems[chartId] = indexes;

    return hiddenItems;
  }, Object.create(null) as HiddenChartItems);
}

export function collectHiddenChartItems(charts: ChartConfig[]): HiddenChartItems {
  return charts.reduce<HiddenChartItems>((hiddenItems, chart) => {
    const chartInstance = ChartJS.getChart(chart.id);
    if (!chartInstance) return hiddenItems;

    const itemsSize =
      chart.type === 'data' ? chartInstance.data.labels?.length ?? 0 : chartInstance.data.datasets.length;
    const isItemVisible = (index: number) =>
      chart.type === 'data' ? chartInstance.getDataVisibility(index) : chartInstance.isDatasetVisible(index);

    const hiddenIndexes = Array.from({ length: itemsSize }, (_, index) => index).filter(
      (index) => !isItemVisible(index)
    );
    if (hiddenIndexes.length) hiddenItems[chart.id] = hiddenIndexes;

    return hiddenItems;
  }, {});
}

/**
 * Returns `search` with the currently hidden chart legend items appended,
 * read from the live Chart.js instances. Used to build the export tab URL
 * at click time, since legend toggles live only in chart component state.
 */
export function appendHiddenChartItems(search: string, charts: ChartConfig[]): string {
  const hiddenItems = collectHiddenChartItems(charts);
  const searchParams = new URLSearchParams(search);

  if (Object.keys(hiddenItems).length) {
    searchParams.set(HIDDEN_CHART_ITEMS_PARAM, serializeHiddenChartItems(hiddenItems));
  } else {
    // a stale parameter (e.g. from an opened export URL) must not re-hide items the user has shown again
    searchParams.delete(HIDDEN_CHART_ITEMS_PARAM);
  }

  const serializedSearch = searchParams.toString();
  return serializedSearch ? `?${serializedSearch}` : '';
}
